import { Language } from 'codegen/types'
import type { LocalizedStrings } from 'codegen/types'

import { defaultLanguage, languageMap, studioLanguageMap } from './constants'

export type Locales = Exclude<keyof LocalizedStrings, '__typename'>

/**
 * Validates if a given value is a supported marketplace / My Account locale.
 * @param locale
 */
export function validateLocale(locale: unknown): locale is Language {
  return languageMap.has(locale as Language)
}

/**
 * Validates if a given value is a supported Studio locale (marketplace locales
 * plus the Language Push batch). Use this instead of `validateLocale` anywhere
 * that reads/writes the language cookie for Studio and its sister apps
 * (freemode, lumo), so those locales round-trip instead of being rejected back
 * to `defaultLanguage`.
 * @param locale
 */
export function validateStudioLocale(locale: unknown): locale is Language {
  return studioLanguageMap.has(locale as Language)
}

/** GraphQL Language enum → first URL path segment for Studio-family SPAs. */
const STUDIO_LANGUAGE_TO_URL_SEGMENT: Partial<Record<string, string>> = {
  zh_Hans: 'zh',
  zh_Hant: 'zh-tw',
}

const STUDIO_URL_SEGMENT_TO_LANGUAGE: Record<string, Language> = {
  zh: Language.ZhHans,
  'zh-tw': Language.ZhHant,
}

/** Enum values that were briefly used as URL segments before pretty Chinese codes. */
const STUDIO_LEGACY_URL_LOCALE_SEGMENTS = new Set(['zh_Hans', 'zh_Hant'])

export function studioLanguageToUrlSegment(language: string): string {
  return STUDIO_LANGUAGE_TO_URL_SEGMENT[language] ?? language
}

/**
 * URL locale segments for the Studio language set — the `locales` list for
 * Studio-family Next.js apps, whose counterpart for the marketplace set is
 * `supportedLanguages`.
 *
 * Segments, not enum values, so `zh_Hant` appears as `zh-tw`. That means a
 * `lang` cookie holding a Chinese enum value will not match this list, which is
 * why `getRedirectUrl` leaves those readers on the unprefixed pages; every other
 * Studio locale round-trips.
 */
export const studioUrlLocaleSegments: string[] = Array.from(studioLanguageMap.keys()).map(
  studioLanguageToUrlSegment,
)

function normalizeStudioUrlSegmentForLookup(segment: string): string {
  if (segment.toLowerCase() === 'zh-tw') {
    return 'zh-tw'
  }

  return segment
}

/** Resolves a URL path segment to the GraphQL Language enum (cookie / API value). */
export function studioUrlSegmentToLanguage(segment: string): Language | undefined {
  const normalized = normalizeStudioUrlSegmentForLookup(segment)
  const mapped = STUDIO_URL_SEGMENT_TO_LANGUAGE[normalized]

  if (mapped) {
    return mapped
  }

  if (validateStudioLocale(segment)) {
    return segment
  }

  return undefined
}

/**
 * Whether the first path segment is a supported Studio URL locale (not a legacy
 * enum slug). Takes `unknown` and narrows, so callers holding a possibly-absent
 * route param — next-intl's `requestLocale`, a `[locale]` param — can use it as
 * the guard without a separate `typeof` check.
 */
export function isStudioUrlLocaleSegment(segment: unknown): segment is string {
  if (typeof segment !== 'string') {
    return false
  }

  const normalized = normalizeStudioUrlSegmentForLookup(segment)

  if (normalized in STUDIO_URL_SEGMENT_TO_LANGUAGE) {
    return true
  }

  return validateStudioLocale(segment) && !STUDIO_LEGACY_URL_LOCALE_SEGMENTS.has(segment)
}

export function isStudioLegacyUrlLocaleSegment(segment: string): boolean {
  return STUDIO_LEGACY_URL_LOCALE_SEGMENTS.has(segment)
}

/**
 * Compact codes for fixed-width UI badges (e.g. the Studio language switcher
 * trigger, sized for a 2-character code). ISO 639-1 only has one code for
 * Chinese (`zh`), so simplified/traditional fall back to the common
 * region-based shorthand instead of the full `zh_Hans` / `zh_Hant` enum value.
 */
const LANGUAGE_BADGE_CODE: Record<string, string> = {
  zh_Hans: 'CN',
  zh_Hant: 'TW',
}

/**
 * Returns a short, fixed-length code suitable for compact language badges.
 * Falls back to uppercasing the locale itself for locales that are already
 * short (every locale except the Chinese script variants).
 */
export function toLanguageBadgeCode(locale: string): string {
  return LANGUAGE_BADGE_CODE[locale] ?? locale.toUpperCase()
}

/**
 * Converts a locale to a language
 * @param locale Must be one of the supported locales defined in `locales`
 */
export function localeToLanguage(locale?: string) {
  return validateLocale(locale) ? locale : defaultLanguage
}

/**
 * Narrows an arbitrary value to a supported `Locales`, falling back to the
 * default language. Use this when a value typed as the full GraphQL `Language`
 * enum (a superset of the supported locales) needs to be used where a `Locales`
 * is expected — for example indexing localized strings or passing to components
 * that only handle supported locales.
 */
export function toLocale(locale?: unknown): Locales {
  return (validateLocale(locale) ? locale : defaultLanguage) as Locales
}

function fixPathname(pathname: string, lang?: Language) {
  const segments = pathname.split('/').filter(Boolean)

  // If we do not have a preferred lang, leave it unchanged
  if (lang) {
    if (validateLocale(segments[0])) {
      segments[0] = lang
    } else {
      segments.unshift(lang)
    }
  }

  let newPathname = segments.join('/')

  if (!newPathname.startsWith('/')) {
    newPathname = `/${newPathname}`
  }

  if (!newPathname.endsWith('/')) {
    newPathname = `${newPathname}/`
  }

  if (newPathname.startsWith('/en/')) {
    newPathname = newPathname.replace('/en/', '/')
  }

  return newPathname
}

/**
 * Adds a locale to a path
 * @param url Can be a relative path or an absolute URL
 * @param lang Language enum value
 */
export function addOrReplaceLanguageInUrl(url: string, lang?: Language): string {
  // Try to parse the path as a URL
  // If we get an error, it means that path is relative
  try {
    const urlObj = new URL(url)

    urlObj.pathname = fixPathname(urlObj.pathname, lang)

    return urlObj.toString()
    // oxlint-disable-next-line unicorn/catch-error-name
  } catch (err) {
    try {
      const TEMP_BASE = 'https://temp-base.com'
      const urlObj = new URL(url, TEMP_BASE)

      urlObj.pathname = fixPathname(urlObj.pathname, lang)

      return urlObj.toString().replace(TEMP_BASE, '')
      // oxlint-disable-next-line unicorn/catch-error-name
    } catch (err) {
      return `${lang === defaultLanguage ? '' : `/${lang}`}${url}`
    }
  }
}

/**
 * Extracts the language segment from a URL
 * @param url Must be an absolute URL
 */
export function getLanguageSegment(url: string) {
  try {
    const [locale] = new URL(url).pathname.split('/').filter(Boolean)

    if (validateLocale(locale)) {
      return locale
    }
  } catch (error) {}

  return defaultLanguage
}

/**
 * Adds a locale to a path
 * @param url Can be a relative path or an absolute URL
 * @param locale Must be one of the supported locales defined in `locales`
 */
export function addOrReplaceLocaleInUrl(url: string, locale?: string) {
  return addOrReplaceLanguageInUrl(url, localeToLanguage(locale))
}

/**
 * Extracts the locale segment from a pathname
 * @param pathname Must be a relative URL
 */
export function getPathnameWithoutLocale(pathname: string) {
  const segments = pathname.split('/').filter(Boolean)

  if (validateLocale(segments[0])) {
    return fixPathname(segments.slice(1).join('/'))
  }

  return fixPathname(pathname)
}
